<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'JobHandlerControl',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits([
    'reload',
    'close',
    'save',
    'success',
    'pending',
    'running',
    'completed',
    'failed',
    'cancelled',
    'deleted',
  ]);

  const props = defineProps({
    block: {
      type: Boolean,
      required: false,
      default: false,
    },
    jobType: {
      type: String,
      required: true,
    },
    text: {
      type: String,
      required: true,
    },
    loadCurrent: {
      type: Boolean,
      required: false,
      default: false,
    },
    payload: {
      type: Function,
      required: true,
    },
    jobId: {
      type: String,
      required: false,
    },
    icon: {
      type: String,
      required: false,
    },
    variant: {
      type: String,
      required: false,
      default: 'light',
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    size: {
      type: String,
      required: false,
      default: 'md',
    },
    tooltip: {
      type: String,
      required: false,
    },
    pollingInterval: {
      type: Number,
      required: false,
      default: 1000,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const loadJob = (...args) => {
    return $fn.value
      .fetch('read_a_job', { parameters: { id: jobData.value.id } })
      .then((data) => {
        setData('jobData', data);
        setData('status', data.status);
        maybeStopPoll();
      })
      .catch((error) => handleError(error));
  };
  const onStatusReceived = (...args) => {
    const [{ status: newStatus }] = args;

    if (newStatus && status.value != newStatus) {
      setData('jobData.status', newStatus);
      setData('status', newStatus);
      emit(newStatus);

      if (newStatus !== 'running') {
        return loadJob();
      }
    }
  };
  const onSubmit = (...args) => {
    const val = _.isFunction(props.payload) ? props.payload() : props.payload;

    if (isRunning.value) {
      return;
    }

    Promise.resolve(val)
      .then((payload) => {
        if (!payload || payload.cancel) {
          return;
        }

        $fn.value
          .crud('a_job')
          .create(payload, { parameters: { job_type: props.jobType } })
          .then((resource) => {
            setData('jobData', resource);
            setData('status', resource.status);
          })
          .then(() => maybeStartPoll())
          .catch((error) => handleError(error));
      })
      .catch((error) => handleError(error));
  };
  const checkStatus = (...args) => {
    if (!jobData.value) {
      return false;
    }

    const currentStatus = jobData.value.status;

    if (currentStatus && status.value != currentStatus) {
      // status change
      emit(currentStatus);
      setData('status', currentStatus); // Safely set the data
    }

    if (currentStatus !== 'pending' && currentStatus !== 'running') {
      console.log(
        `JobStatus is ${currentStatus}, stop polling.`,
        jobPoller.value
      );
      jobPoller.value.stop();
      emit('success');
      return;
    }

    return $fn.value
      .fetch('read_a_job_status', { parameters: { id: jobData.value.id } })
      .then((data) => {
        onStatusReceived(data);
      });
  };
  const maybeStartPoll = (...args) => {
    const currentStatus = status.value;

    if (
      ['completed', 'failed', 'cancelled', 'deleted'].indexOf(currentStatus) >
      -1
    ) {
      return;
    }

    if (!jobPoller.value.isRunning) {
      return jobPoller.value.start();
    }
  };
  const maybeStopPoll = (...args) => {
    const currentStatus = status.value;

    if (['pending', 'running'].indexOf(currentStatus) > -1) {
      return;
    }

    if (jobPoller.value.isRunning) {
      return jobPoller.value.stop();
    }
  };

  const messages = ref({
    showJobTooltip: $fn.value.pgettext(
      'JobHandler|Command',
      'Open job details'
    ),
    reloadJobData: $fn.value.pgettext(
      'JobHandler|Command',
      'Reload job status'
    ),
  });
  const jobData = ref(null);
  const jobPoller = ref(null);
  const status = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    jobData: jobData.value,
    jobPoller: jobPoller.value,
    status: status.value,
    ux: ux.value,
  }));

  const $$load_jobData = async () => {
    return props.loadCurrent
      ? $fn.value
          .fetch('read_a_job', {
            parameters: {
              id: props.jobId || 'current',
              job_type: props.jobType,
            },
            asResource: true,
          })
          .then((job) =>
            job && (job.status === 'pending' || job.status === 'running')
              ? job
              : null
          )
          .catch(() => null)
      : null;
  };
  const $$load_jobPoller = async () => {
    return $fn.value.poller({
      id: 'Job',
      callback: checkStatus,
      interval: props.pollingInterval,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'jobData' },
      { name: 'jobPoller' },
      { name: 'status' },
      { name: 'ux' },
    ],
    loaders: { jobData: $$load_jobData, jobPoller: $$load_jobPoller },
    ux: {},
    dataRefs: { messages, jobData, jobPoller, status, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isRunning = computed(() => {
    if (loaded.value) {
      return status.value === 'running';
    }
    return null;
  });
  const isPending = computed(() => {
    if (loaded.value) {
      return status.value === 'pending';
    }
    return null;
  });
  const spinning = computed(() => {
    return (
      isRunning.value ||
      isPending.value ||
      (actions && actions.reloadJobData && actions.reloadJobData.isRunning)
    );
  });

  watch(
    () => props.block,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('block');
    }
  );
  watch(
    () => props.jobType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('jobType');
    }
  );
  watch(
    () => props.text,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('text');
    }
  );
  watch(
    () => props.loadCurrent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('loadCurrent');
    }
  );
  watch(
    () => props.payload,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('payload');
    }
  );
  watch(
    () => props.jobId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('jobId');
    }
  );
  watch(
    () => props.icon,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('icon');
    }
  );
  watch(
    () => props.variant,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('variant');
    }
  );
  watch(
    () => props.disabled,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('disabled');
    }
  );
  watch(
    () => props.size,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('size');
    }
  );
  watch(
    () => props.tooltip,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('tooltip');
    }
  );
  watch(
    () => props.pollingInterval,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('pollingInterval');
    }
  );

  const act_2248226175642056_script_39fqnzsak_value_callable = async (
    $event
  ) => {
    return onSubmit();
  };
  const act_2248226175642056_script_39fqnzsak_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_39fqnzsak_value_callable($event);
      },
    };
  };
  const act_2248226175642056_script_39fqnzsak_getWhen = async ($event) => {
    return !props.disabled;
  };
  const act_1040167445267876_script_64bnp3751_value_callable = async (
    $event
  ) => {
    return onSubmit();
  };
  const act_1040167445267876_script_64bnp3751_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_64bnp3751_value_callable($event);
      },
    };
  };
  const act_1040167445267876_script_64bnp3751_getWhen = async ($event) => {
    return !props.disabled;
  };
  const act_4044930568678877_showModal_ujct8eqb3_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Job',
      props: {
        oid: jobData.value.id,
      },
    };
  };
  const act_1073564360475282_setData_nmw0exkfq_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'jobData',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1073564360475282_setData_nmw0exkfq_getWhen = async ($event) => {
    return $event.data.response.data;
  };
  const act_1073564360475282_request_1upu441ji_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'read_a_job',
      parameters: {
        id: jobData.value.id,
      },
    };
  };

  const act_2248226175642056_script_39fqnzsak = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_39fqnzsak_getActionArgs,
    when: act_2248226175642056_script_39fqnzsak_getWhen,
    displayName: 'submit',
  });
  const inst_evh_2248226175642056_clickPreventStop_fxtr8wcps =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_39fqnzsak,
      event: 'click.prevent.stop',
      displayName: 'submit',
    });
  const inst_evh_2248226175642056_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_clickPreventStop_fxtr8wcps],
      event: 'click.prevent.stop',
    });
  const act_1040167445267876_script_64bnp3751 = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_64bnp3751_getActionArgs,
    when: act_1040167445267876_script_64bnp3751_getWhen,
    displayName: 'submit',
  });
  const inst_evh_1040167445267876_splitClick_1o1afwjmz =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_64bnp3751,
      event: 'split-click',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_splitClick =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_splitClick_1o1afwjmz],
      event: 'split-click',
    });
  const act_4044930568678877_showModal_ujct8eqb3 =
    new actionTypes.ShowModalAction({
      actionArgs: act_4044930568678877_showModal_ujct8eqb3_getActionArgs,
      displayName: 'showJob',
    });
  const inst_evh_4044930568678877_clickPreventStop_o7f6ahkzu =
    new actionTypes.EventHandlerImpl({
      action: act_4044930568678877_showModal_ujct8eqb3,
      event: 'click.prevent.stop',
      displayName: 'showJob',
    });
  const inst_evh_4044930568678877_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4044930568678877_clickPreventStop_o7f6ahkzu],
      event: 'click.prevent.stop',
    });
  const act_1073564360475282_setData_nmw0exkfq = new actionTypes.SetDataAction({
    actionArgs: act_1073564360475282_setData_nmw0exkfq_getActionArgs,
    when: act_1073564360475282_setData_nmw0exkfq_getWhen,
  });
  const inst_evh_1073564360475282_success_mfngwnhq8 =
    new actionTypes.EventHandlerImpl({
      action: act_1073564360475282_setData_nmw0exkfq,
      event: 'success',
      displayName: 'setData',
    });
  const act_1073564360475282_request_1upu441ji = new actionTypes.RequestAction({
    actionArgs: act_1073564360475282_request_1upu441ji_getActionArgs,
    displayName: 'reloadJobData',
    events: [inst_evh_1073564360475282_success_mfngwnhq8],
  });
  const inst_evh_1073564360475282_clickPreventStop_4l1m48duo =
    new actionTypes.EventHandlerImpl({
      action: act_1073564360475282_request_1upu441ji,
      event: 'click.prevent.stop',
      displayName: 'reloadJobData',
    });
  const inst_evh_1073564360475282_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1073564360475282_clickPreventStop_4l1m48duo],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_2248226175642056_clickPreventStop_fxtr8wcps,
    submit: inst_evh_2248226175642056_clickPreventStop_fxtr8wcps,
    inst_evh_2248226175642056_clickPreventStop,
    inst_evh_1040167445267876_splitClick_1o1afwjmz,
    inst_evh_1040167445267876_splitClick,
    inst_evh_4044930568678877_clickPreventStop_o7f6ahkzu,
    showJob: inst_evh_4044930568678877_clickPreventStop_o7f6ahkzu,
    inst_evh_4044930568678877_clickPreventStop,
    inst_evh_1073564360475282_success_mfngwnhq8,
    inst_evh_1073564360475282_clickPreventStop_4l1m48duo,
    reloadJobData: inst_evh_1073564360475282_clickPreventStop_4l1m48duo,
    inst_evh_1073564360475282_clickPreventStop,
  };

  const evh_2248226175642056_clickPreventStop_fxtr8wcps = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_clickPreventStop_fxtr8wcps.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_splitClick_1o1afwjmz = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_splitClick_1o1afwjmz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_splitClick = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_splitClick.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4044930568678877_clickPreventStop_o7f6ahkzu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4044930568678877_clickPreventStop_o7f6ahkzu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4044930568678877_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4044930568678877_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1073564360475282_success_mfngwnhq8 = async (event, scope) => {
    return await actions.inst_evh_1073564360475282_success_mfngwnhq8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1073564360475282_clickPreventStop_4l1m48duo = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1073564360475282_clickPreventStop_4l1m48duo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1073564360475282_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1073564360475282_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div>
    <ItpButton
      :text="text"
      :icon="icon"
      :variant="variant"
      :spinning="spinning"
      :disabled="disabled"
      :size="size"
      :tooltip="tooltip"
      :block="block"
      class="app-JobHandlerControl"
      v-if="loaded && !jobData"
      @click.prevent.stop="evh_2248226175642056_clickPreventStop($event, {})"
    >
    </ItpButton>
    <div class="input-group app-JobHandlerControl">
      <BDropdown
        split
        :size="size"
        :title="tooltip"
        :variant="variant"
        :disabled="disabled"
        split-class="border"
        toggle-class="border"
        v-if="loaded && jobData"
        @split-click="evh_1040167445267876_splitClick($event, {})"
      >
        <template #button-content>
          <ItpBox alignItems="center">
            <BSpinner small type="grow" class="me-1" v-if="spinning">
            </BSpinner>
            <ItpIcon :name="icon" class="me-1" v-if="icon"> </ItpIcon>
            <span :class="['text-nowrap', status ? 'me-2' : null]" v-if="text">
              {{ text }}
            </span>
            <UcStatusBadge :status="status" v-if="status"> </UcStatusBadge>
          </ItpBox>
        </template>
        <BDropdownItem
          @click.prevent.stop="
            evh_4044930568678877_clickPreventStop($event, {})
          "
        >
          {{ messages.showJobTooltip }}...
        </BDropdownItem>
        <BDropdownItem
          @click.prevent.stop="
            evh_1073564360475282_clickPreventStop($event, {})
          "
        >
          {{ messages.reloadJobData }}
        </BDropdownItem>
      </BDropdown>
    </div>
  </div>
</template>
